import { type RouteObject } from "react-router-dom";

import { RecapTransportGate } from "./recap-transport-gate";
import { buildAppRouter, type Loader, page, preferencesRoute } from "./shared";

// Route with index page + dynamic :id detail page (most common pattern)
function listDetail(
  path: string,
  listImport: Loader,
  detailImport: Loader,
  extra?: RouteObject[],
): RouteObject {
  return {
    path,
    children: [
      { index: true, ...page(listImport) },
      { path: ":id", ...page(detailImport) },
      ...(extra ?? []),
    ],
  };
}

// Full desktop/web route tree. Wrapped in a factory so the mobile target can
// tree-shake it out entirely (see ./index.ts).
export function createWebRouter() {
  return buildAppRouter(
    [
      { path: "home", ...page(() => import("@/app/home/gate")) },
      // Design-system showcase — living token/primitive reference + DA
      // refinement proposal. No sidebar entry, no flag; lazy so it costs
      // nothing until visited (see STK-1509).
      {
        path: "design-system",
        ...page(() => import("@/app/design-system/page")),
      },
      // CRUD-style routes (list + detail)
      listDetail(
        "orders",
        () => import("@/app/orders/page"),
        () => import("@/app/orders/[id]/page"),
      ),
      listDetail(
        "stocks",
        () => import("@/app/stocks/page"),
        () => import("@/app/stocks/[id]/page"),
      ),
      listDetail(
        "flows",
        () => import("@/app/flows/page"),
        () => import("@/app/flows/[id]/page"),
      ),
      // Purchases workspace — shares the recap shell + primitives, but
      // mounts under a purchases-owned wrapper that pins mode + basePath.
      // Navigation reads basePath from context so row clicks land at
      // /purchases/:id (not /recap/:id) without per-component mode-awareness.
      {
        path: "purchases",
        ...page(
          () => import("@/app/purchases/components/PurchaseWorkspaceWrapper"),
        ),
        children: [
          { index: true, ...page(() => import("@/app/purchases/page")) },
          {
            path: ":id",
            ...page(() => import("@/app/purchases/[id]/page")),
          },
        ],
      },
      listDetail(
        "saved-offers",
        () => import("@/app/saved-offers/page"),
        () => import("@/app/saved-offers/[id]/page"),
      ),
      listDetail(
        "lots",
        () => import("@/app/lots/page"),
        () => import("@/app/lots/[id]/page"),
      ),
      listDetail(
        "operations",
        () => import("@/app/operations/page"),
        () => import("@/app/operations/[id]/page"),
      ),
      listDetail(
        "agent-logs",
        () => import("@/app/agent-logs/page"),
        () => import("@/app/agent-logs/[id]/page"),
      ),

      listDetail(
        "profile",
        () => import("@/app/profile/page"),
        () => import("@/app/profile/[id]/page"),
      ),

      listDetail(
        "partners",
        () => import("@/app/partners/page"),
        () => import("@/app/partners/[id]/page"),
        [
          {
            path: ":id/pricing",
            ...page(() => import("@/app/partners/[id]/pricing/page")),
          },
        ],
      ),

      listDetail(
        "products",
        () => import("@/app/products/page"),
        () => import("@/app/products/[id]/page"),
        [
          {
            path: "total-stocks",
            ...page(() => import("@/app/products/total-stocks/page")),
          },
          {
            path: ":id/stat",
            ...page(() => import("@/app/products/[id]/components/stat/page")),
          },
        ],
      ),

      // Chat (list + sub-route, not :id)
      {
        path: "chat",
        children: [
          { index: true, ...page(() => import("@/app/chat/page")) },
          {
            path: "unanswered",
            ...page(() => import("@/app/chat/unanswered/page")),
          },
        ],
      },

      // `/recap` is transport-aware: transport tenants (the `transportMode`
      // flag) are redirected to the `/recap_transport` sibling by
      // `RecapTransportGate`; sales tenants keep the sales workspace. The
      // redirect is flag-gated on purpose — an unconditional one made the sales
      // recap unreachable for every sales tenant. The gate wraps the sales
      // pages, which stay lazy via a pathless layout child so the workspace is
      // not pulled into the router bundle.
      {
        path: "recap",
        element: <RecapTransportGate />,
        children: [
          {
            lazy: () =>
              import("@/app/recap/components/RecapSalesWorkspaceWrapper").then(
                (m) => ({ Component: m.default }),
              ),
            children: [
              { index: true, ...page(() => import("@/app/recap/page")) },
              {
                path: "mobile",
                ...page(() => import("@/app/recap/mobile/page")),
              },
              {
                path: ":id",
                ...page(() => import("@/app/recap/[id]/page")),
              },
            ],
          },
        ],
      },
      // Transport recap mounts its own workspace wrapper — without it,
      // RecapShell's `useChatPanelActions()` throws because nothing hosts
      // the chat panel context above the `/recap_transport` routes.
      {
        path: "recap_transport",
        ...page(
          () =>
            import(
              "@/app/recap_transport/components/TransportWorkspaceWrapper"
            ),
        ),
        children: [
          {
            index: true,
            ...page(() => import("@/app/recap_transport/page")),
          },
          {
            path: ":id",
            ...page(() => import("@/app/recap_transport/[id]/page")),
          },
        ],
      },
      // Company-scoped AI monitoring dashboard (OWNER-gated). Standalone
      // top-level surface, re-homed out of the transport-admin shell it was
      // originally built as a tab of.
      {
        path: "ai-monitoring",
        children: [
          {
            index: true,
            ...page(() => import("@/app/ai-monitoring/page")),
          },
          {
            path: "accuracy",
            ...page(() => import("@/app/ai-monitoring/accuracy/page")),
          },
          {
            path: "speed",
            ...page(() => import("@/app/ai-monitoring/speed/page")),
          },
          {
            path: "funnel",
            ...page(() => import("@/app/ai-monitoring/funnel/page")),
          },
        ],
      },
      // Scoped monitoring dashboard (STK-1191). Company admins see company-wide
      // metrics, group admins see their group(s); the backend resolves scope
      // from the caller's identity.
      {
        path: "monitoring",
        ...page(() => import("@/app/monitoring/page")),
      },
      {
        path: "daily_recap/:date",
        ...page(() => import("@/app/daily_recap/[date]/page")),
      },

      // CRM
      { path: "crm/:id", ...page(() => import("@/app/crm/[id]/page")) },
      {
        path: "product-crm/:id",
        ...page(() => import("@/app/product-crm/[id]/page")),
      },

      // Client groups
      {
        path: "client-groups",
        children: [
          {
            index: true,
            ...page(() => import("@/app/client-groups/page")),
          },
          {
            path: ":id/pricing",
            ...page(() => import("@/app/client-groups/[id]/pricing/page")),
          },
        ],
      },

      // Reports
      {
        path: "reports",
        children: [
          { index: true, ...page(() => import("@/app/reports/page")) },
          {
            path: "pending",
            ...page(() => import("@/app/reports/pending/page")),
          },
          {
            path: "profitability",
            ...page(() => import("@/app/reports/profitability/page")),
          },
          {
            path: "returns",
            ...page(() => import("@/app/reports/returns/page")),
          },
          {
            path: "turnover",
            ...page(() => import("@/app/reports/turnover/page")),
          },
        ],
      },

      // Preferences
      preferencesRoute(),

      // Parameters
      {
        path: "parameters",
        children: [
          { index: true, ...page(() => import("@/app/parameters/page")) },
          {
            path: "locations",
            ...page(() => import("@/app/parameters/locations/page")),
          },
        ],
      },

      // Simple pages
      { path: "contacts", ...page(() => import("@/app/contacts/page")) },
      { path: "routes", ...page(() => import("@/app/routes/page")) },
      {
        path: "steering",
        lazy: () =>
          import("@/app/steering/layout").then((m) => ({
            Component: m.default,
          })),
        children: [
          {
            index: true,
            ...page(() => import("@/app/steering/page")),
          },
          {
            path: ":id",
            ...page(() => import("@/app/steering/[id]/page")),
          },
        ],
      },
      { path: "pricing", ...page(() => import("@/app/pricing/page")) },
      { path: "units", ...page(() => import("@/app/units/page")) },
      { path: "emails", ...page(() => import("@/app/emails/page")) },
      {
        path: "message-settings",
        ...page(() => import("@/app/message-settings/page")),
      },
      { path: "company", ...page(() => import("@/app/company/page")) },
      // Dedicated group-admin surface ("Your groups" → per-group management),
      // gated in the sidebar by `sidebarShowGroups` + group-admin membership.
      // The company owner still manages every group from /company?tab=groups;
      // this page is the group admin / HR entry point.
      { path: "groups", ...page(() => import("@/app/groups/page")) },
      { path: "groups/:id", ...page(() => import("@/app/groups/page")) },
      { path: "ressere", ...page(() => import("@/app/ressere/page")) },
      { path: "ai", ...page(() => import("@/app/ai/page")) },
      // ai-admin also available at /superadmin/ai-management (STK-1295)
      {
        path: "ai-admin",
        ...page(() => import("@/app/ai-admin/page")),
      },
      {
        path: "workflows",
        ...page(() => import("@/app/workflows/page")),
      },
      { path: "feedback", ...page(() => import("@/app/feedback/page")) },
      { path: "dictionary", ...page(() => import("@/app/dictionary/page")) },
      {
        path: "ai-proposals",
        ...page(() => import("@/app/ai-proposals/page")),
      },
      {
        path: "ai-pre-orders",
        ...page(() => import("@/app/ai-pre-orders/page")),
      },
      { path: "seeding", ...page(() => import("@/app/seeding/page")) },

      // Dev-only: synthetic error to test the error boundary. No UI link.
      {
        path: "dev/throw",
        ...page(() => import("@/app/dev/throw/page")),
      },
    ],
    [
      // ── Superadmin (top-level, own layout — not under AuthLayout) ──
      {
        path: "superadmin",
        ...page(() => import("@/app/superadmin/layout")),
        children: [
          { index: true, ...page(() => import("@/app/superadmin/page")) },
          {
            path: "companies",
            ...page(() => import("@/app/superadmin/companies/page")),
          },
          {
            path: "companies/:id",
            ...page(() => import("@/app/superadmin/companies/[id]/page")),
          },
          {
            // Pathless Workforce layout: groups Users/Employees/Groups under a
            // shared sub-tab bar without changing their URLs.
            ...page(() => import("@/app/superadmin/workforce/layout")),
            children: [
              {
                path: "users",
                ...page(() => import("@/app/superadmin/users/page")),
              },
              {
                path: "employees",
                ...page(() => import("@/app/superadmin/employees/page")),
              },
              {
                path: "groups",
                children: [
                  {
                    index: true,
                    ...page(() => import("@/app/superadmin/groups/page")),
                  },
                  {
                    path: ":companyId/:groupId",
                    ...page(
                      () =>
                        import(
                          "@/app/superadmin/groups/[companyId]/[groupId]/page"
                        ),
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: "messaging",
            ...page(() => import("@/app/superadmin/messaging/page")),
          },
          {
            path: "usage",
            ...page(() => import("@/app/superadmin/usage/page")),
          },
          {
            path: "northstar",
            ...page(() => import("@/app/superadmin/northstar/page")),
          },
          {
            path: "feature-flags",
            ...page(() => import("@/app/superadmin/feature-flags/page")),
          },
          {
            path: "ai-management",
            ...page(() => import("@/app/superadmin/ai-management/layout")),
            children: [
              {
                index: true,
                ...page(() => import("@/app/superadmin/ai-management/page")),
              },
              {
                path: "executions",
                ...page(
                  () =>
                    import("@/app/superadmin/ai-management/executions/page"),
                ),
              },
              {
                path: "workflows",
                ...page(
                  () => import("@/app/superadmin/ai-management/workflows/page"),
                ),
              },
              {
                path: "triggers",
                ...page(
                  () => import("@/app/superadmin/ai-management/triggers/page"),
                ),
              },
              {
                path: "run-on-chats",
                ...page(
                  () =>
                    import("@/app/superadmin/ai-management/run-on-chats/page"),
                ),
              },
              {
                path: "scripts",
                ...page(
                  () => import("@/app/superadmin/ai-management/scripts/page"),
                ),
              },
              {
                path: "tools",
                ...page(
                  () => import("@/app/superadmin/ai-management/tools/page"),
                ),
              },
              {
                path: "manual",
                ...page(
                  () => import("@/app/superadmin/ai-management/manual/page"),
                ),
              },
              {
                path: "context",
                ...page(
                  () => import("@/app/superadmin/ai-management/context/page"),
                ),
              },
              {
                path: "monitoring",
                ...page(
                  () =>
                    import("@/app/superadmin/ai-management/monitoring/page"),
                ),
              },
              {
                path: "settings",
                ...page(
                  () => import("@/app/superadmin/ai-management/settings/page"),
                ),
              },
            ],
          },
        ],
      },
      {
        path: "ai-superadmin",
        ...page(() => import("@/app/ai-superadmin/layout")),
        children: [
          {
            index: true,
            ...page(() => import("@/app/ai-superadmin/page")),
          },
          {
            path: "accuracy",
            ...page(() => import("@/app/ai-superadmin/accuracy/page")),
          },
          {
            path: "cost",
            ...page(() => import("@/app/ai-superadmin/cost/page")),
          },
          {
            path: "speed",
            ...page(() => import("@/app/ai-superadmin/speed/page")),
          },
          {
            path: "funnel",
            ...page(() => import("@/app/ai-superadmin/funnel/page")),
          },
          {
            path: "tools",
            ...page(() => import("@/app/ai-superadmin/tools/page")),
          },
          {
            path: "modified-fields",
            ...page(() => import("@/app/ai-superadmin/modified-fields/page")),
          },
          {
            path: "context",
            ...page(() => import("@/app/ai-superadmin/context/page")),
          },
          {
            path: "executions/:id",
            ...page(() => import("@/app/ai-superadmin/executions/[id]/page")),
          },
        ],
      },
    ],
  );
}
